import { defineStore } from "pinia";
import { MateTraitAnimation } from "@/types/entity";
import api from "@/modules/api";
import { TraitAnimationPortraitType, TraitAnimationType } from "@/enums";
import { useMate } from "@/stores/mate";
import i18n from "@/locales";

export interface TraitAnimationState {
  animationMap: Record<number, MateTraitAnimation>; // animationId 2 animation
  traitId2AnimationKeyList: Record<number, number[]>; // traitId 2 animationId list
  initializedTraitIds: number[]; // traitId list
}

export const useAnimationTypeText = (type: TraitAnimationType) => {
  switch (type) {
    case 1:
      return i18n.global.t("Animation.type.blink");
    case 2:
    default:
      return i18n.global.t("Animation.type.lipSync");
  }
};
export const useAnimationPortraitTypeText = (
  type: TraitAnimationPortraitType
) => {
  switch (type) {
    case 1:
      return i18n.global.t("Animation.portraitType.openEyes");
    case 2:
      return i18n.global.t("Animation.portraitType.closeEyes");
    case 3:
      return i18n.global.t("Animation.portraitType.openMouth");
    case 4:
      return i18n.global.t("Animation.portraitType.closeMouth");
    case 5:
    default:
      return i18n.global.t("Animation.portraitType.halfOpenMouth");
  }
};

export const useTraitAnimation = defineStore("traitAnimation", {
  state: (): TraitAnimationState => ({
    animationMap: {},
    traitId2AnimationKeyList: {},
    initializedTraitIds: [],
  }),
  actions: {
    async getOrFetchAnimationByMateId(mateId: number, traitId?: number) {
      const res = await api.readMateTraitAnimationPage({
        filter: { mateId, mateTraitId: traitId },
        sort: { order: "desc", field: "dtUpdate" },
        pagination: { page: 1, perPage: 999 },
      });
      const animationList = res.page.data;
      const mateStore = useMate();
      if (mateStore.currentMate!.id === mateId) {
        animationList.reduce((acc, animation) => {
          if (!acc[animation.mateTraitId]) {
            acc[animation.mateTraitId] = [];
          }
          acc[animation.mateTraitId].push(animation.id);
          return acc;
        }, this.traitId2AnimationKeyList);
      }
      this.updateMap(animationList);
      return animationList;
    },
    async getOrFetchTraitAnimationByTraitId(
      traitId: number
    ): Promise<MateTraitAnimation[]> {
      // use cache
      if (this.initializedTraitIds.includes(traitId)) {
        return this.traitId2AnimationKeyList[traitId].map(
          (animationId) => this.animationMap[animationId]
        );
      }
      // fetch from server
      const res = await api.readMateTraitAnimationPage({
        filter: { mateTraitId: traitId },
        sort: { order: "desc", field: "dtUpdate" },
        pagination: { page: 1, perPage: 999 },
      });
      const animations = res.page.data;
      this.initializedTraitIds.push(traitId);
      this.traitId2AnimationKeyList[traitId] = animations.map(
        (animation) => animation.id
      );
      this.updateMap(animations);
      return this.traitId2AnimationKeyList[traitId].map(
        (animationId) => this.animationMap[animationId]
      );
    },
    updateMap(animations: MateTraitAnimation[]): void {
      animations.forEach((animation) => {
        const old = this.animationMap[animation.id];
        if (old) {
          this.animationMap[animation.id] = { ...old, ...animation };
        } else {
          this.animationMap[animation.id] = animation;
        }
      });
    },
    buildParamFromTraitAnimation(
      param: Pick<
        MateTraitAnimation,
        "mateTraitId" | "name" | "type" | "portraitzz"
      >
    ) {
      return {
        mateTraitId: param.mateTraitId,
        name: param.name,
        type: param.type,
        portraitzz: param.portraitzz.map((item) => ({
          ...item.portrait,
          type: item.type,
        })),
      };
    },
    async createTraitAnimation(
      param: Pick<
        MateTraitAnimation,
        "mateTraitId" | "name" | "type" | "portraitzz"
      >
    ): Promise<MateTraitAnimation> {
      const traitId = param.mateTraitId;
      const reqParam = this.buildParamFromTraitAnimation(param);
      const res = await api.createOneMateTraitAnimation(reqParam);
      this.updateMap([res.item]);
      this.traitId2AnimationKeyList[traitId].push(res.item.id);
      return this.animationMap[res.item.id];
    },
    async updateTraitAnimation(
      id: number,
      param: Pick<
        MateTraitAnimation,
        "mateTraitId" | "name" | "type" | "portraitzz"
      >
    ): Promise<MateTraitAnimation> {
      const reqParam = this.buildParamFromTraitAnimation(param);
      const res = await api.updateOneMateTraitAnimation(id, reqParam);
      this.updateMap([res.item]);
      return this.animationMap[res.item.id];
    },
    async deleteTraitAnimation(id: number): Promise<void> {
      const traitId = this.animationMap[id].mateTraitId;
      await api.deleteOneMateTraitAnimation(id);
      this.traitId2AnimationKeyList[traitId] = this.traitId2AnimationKeyList[
        traitId
      ].filter((animationId) => animationId !== id);
    },
    async getOrFetchTraitAnimationById(
      id: number
    ): Promise<MateTraitAnimation> {
      if (this.animationMap[id]) {
        return this.animationMap[id];
      }
      const res = await api.readOneMateTraitAnimation(id);
      this.updateMap([res.item]);
      return res.item;
    },
  },
});
